import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Baby, Moon, Heart, ArrowRight, Mail, X, Loader2, AlertCircle, Plus, RefreshCw } from 'lucide-react';
import Logo from '../components/common/Logo';
import MoonIcon from '../components/common/MoonIcon';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import apiService from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foundReport, setFoundReport] = useState(null);
  const [showExistingReportOptions, setShowExistingReportOptions] = useState(false);
  const [creatingNewReport, setCreatingNewReport] = useState(false);

  const handleAccessReport = async () => {
    if (!email.trim()) {
      setError('Por favor, insira seu email');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Email inválido');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Buscar relatório pelo email da mãe usando o novo endpoint
      const report = await apiService.getReportByEmail(email);
      setFoundReport(report);
      setShowExistingReportOptions(true);
    } catch (err) {
      console.error('Erro ao buscar relatório:', err);
      if (err.message.includes('404') || err.message.includes('não encontrado')) {
        setError('Não encontramos um plano associado a este email. Verifique se o email está correto ou entre em contato com sua consultora.');
      } else {
        setError('Erro ao buscar seu relatório. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToReport = () => {
    if (foundReport) {
      // Verificar status e redirecionar para a página correta
      if (foundReport.status === 'approved' || foundReport.status === 'pending_approval') {
        navigate(`/plan/${foundReport.id}`);
      } else if (foundReport.status === 'pending_routine' || foundReport.completed_days < 5) {
        navigate(`/report/${foundReport.id}/routine`);
      } else {
        navigate(`/report/${foundReport.id}`);
      }
    }
  };

  const handleCreateNewReport = async () => {
    setCreatingNewReport(true);
    try {
      // Criar novo relatório com o mesmo email
      const newReport = await apiService.createReport({
        baby_name: 'Novo Bebê',
        mother_email: email
      });
      // Redirecionar para o formulário do novo relatório
      navigate(`/report/${newReport.id}`);
    } catch (err) {
      console.error('Erro ao criar novo relatório:', err);
      setError('Erro ao criar novo registro. Tente novamente.');
    } finally {
      setCreatingNewReport(false);
    }
  };

  const closeModal = () => {
    setShowAccessModal(false);
    setEmail('');
    setError('');
    setFoundReport(null);
    setShowExistingReportOptions(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-[#FAE0D5] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 opacity-20">
        <MoonIcon variant="purple" size="xl" />
      </div>
      <div className="absolute bottom-40 right-20 opacity-10">
        <MoonIcon variant="purple" size="lg" />
      </div>
      
      {/* Stars decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#8F7AC5] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Logo variant="v2SonoRotina" size="md" />
          <Button 
            variant="ghost" 
            className="text-[#30244F] hover:bg-[#8F7AC5]/10 text-sm"
            onClick={() => navigate('/admin')}
          >
            Área Administrativa
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 py-12 md:py-16">
        <div className="text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#D98DAE]/30 text-[#30244F] rounded-full text-sm font-medium mb-4">
              De mãe pra mãe 💜
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#30244F] leading-tight">
              Noites tranquilas
              <br />
              <span className="text-[#8F7AC5]">começam aqui</span>
            </h1>
          </motion.div>

          <motion.p 
            className="text-base md:text-xl text-[#6B5B8C] max-w-2xl mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Registre a rotina do seu bebê por 5 dias e receba um 
            <strong className="text-[#30244F]"> plano de sono personalizado</strong> para 
            toda a família dormir melhor.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              size="lg"
              className="bg-[#8F7AC5] hover:bg-[#30244F] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              onClick={() => setShowAccessModal(true)}
            >
              Acessar Meu Relatório
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>

        {/* Features */}
        <motion.div 
          className="grid md:grid-cols-3 gap-6 md:gap-8 mt-16 md:mt-20 px-2"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {[
            {
              icon: Baby,
              title: 'Registro Simplificado',
              description: 'Anote a rotina do seu bebê de forma fácil e prática, direto do celular'
            },
            {
              icon: Moon,
              title: 'Análise Personalizada',
              description: 'Cada bebê é único. Seu plano será feito especialmente para vocês'
            },
            {
              icon: Heart,
              title: 'Acompanhamento',
              description: 'Suporte de quem entende sua jornada, de mãe pra mãe'
            }
          ].map((feature, i) => (
            <div 
              key={i}
              className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 md:p-8 text-center shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#D98DAE] to-[#8F7AC5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-[#30244F] mb-2">{feature.title}</h3>
              <p className="text-sm md:text-base text-[#6B5B8C]">{feature.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Trust badge */}
        <motion.div 
          className="text-center mt-12 md:mt-16 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-[#9890A8] text-xs md:text-sm">
            Métodos com evidência científica • Adaptação à sua realidade • Leveza na jornada
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-8 text-center text-[#9890A8] text-sm">
        <p>© 2025 Mariana Mello | Sono e Rotina. Todos os direitos reservados.</p>
      </footer>

      {/* Modal de Acesso */}
      <AnimatePresence>
        {showAccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-[#9890A8] hover:text-[#30244F]"
              >
                <X className="w-6 h-6" />
              </button>

              {!foundReport ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#30244F]">Acessar Meu Relatório</h3>
                    <p className="text-sm text-[#6B5B8C] mt-2">
                      Digite o email cadastrado para acessar seu plano de sono
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        className="w-full border-[#FAE0D5] focus:border-[#8F7AC5] py-3 text-center text-lg"
                        onKeyPress={(e) => e.key === 'Enter' && handleAccessReport()}
                      />
                      {error && (
                        <div className="flex items-start gap-2 mt-2 text-red-500 text-sm">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{error}</span>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={handleAccessReport}
                      disabled={loading}
                      className="w-full bg-[#8F7AC5] hover:bg-[#7A68A8] py-3"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Buscando...
                        </>
                      ) : (
                        <>
                          Buscar Meu Plano
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-[#9890A8]">
                      Você deve ter recebido um link da sua consultora. 
                      Se não encontrar seu email, entre em contato.
                    </p>
                  </div>
                </>
              ) : showExistingReportOptions ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#F2ACAA] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Baby className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#30244F]">Registro Encontrado! 💜</h3>
                    <p className="text-sm text-[#6B5B8C] mt-2">
                      Já existe um registro associado a este email
                    </p>
                  </div>

                  {/* Info do registro existente */}
                  <div className="bg-[#FBF4FF] rounded-xl p-4 mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6B5B8C]">Bebê:</span>
                      <span className="font-semibold text-[#30244F]">{foundReport.baby_name}</span>
                    </div>
                    {foundReport.baby_age && (
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#6B5B8C]">Idade no registro:</span>
                        <span className="font-medium text-[#30244F]">{foundReport.baby_age} meses</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6B5B8C]">Status:</span>
                      <span className={`font-medium ${
                        foundReport.status === 'approved' ? 'text-[#7BC47F]' : 'text-[#8F7AC5]'
                      }`}>
                        {foundReport.status === 'approved' ? 'Plano Aprovado ✓' : 
                         foundReport.status === 'pending_approval' ? 'Em aprovação' :
                         foundReport.status === 'pending_routine' ? 'Registrando rotina' :
                         foundReport.status === 'pending_analysis' ? 'Aguardando análise' :
                         'Aguardando início'}
                      </span>
                    </div>
                    {foundReport.created_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B5B8C]">Data do registro:</span>
                        <span className="font-medium text-[#30244F]">{formatDate(foundReport.created_at)}</span>
                      </div>
                    )}
                  </div>

                  {/* Pergunta */}
                  <div className="bg-[#FAE0D5]/30 rounded-xl p-4 mb-4">
                    <p className="text-sm text-[#30244F] text-center font-medium">
                      O que você deseja fazer?
                    </p>
                  </div>

                  {/* Opções */}
                  <div className="space-y-3">
                    <Button
                      onClick={handleGoToReport}
                      className="w-full bg-[#8F7AC5] hover:bg-[#7A68A8] py-3"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      {foundReport.status === 'approved' || foundReport.status === 'pending_approval' 
                        ? 'Ver Plano de Sono' 
                        : 'Continuar Registro Atual'}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#FAE0D5]"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-white text-[#9890A8]">ou</span>
                      </div>
                    </div>

                    <Button
                      onClick={handleCreateNewReport}
                      disabled={creatingNewReport}
                      variant="outline"
                      className="w-full border-[#D98DAE] text-[#D98DAE] hover:bg-[#D98DAE]/10 py-4 h-auto whitespace-normal text-sm"
                    >
                      {creatingNewReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Criando...
                        </>
                      ) : (
                        <span className="flex flex-col items-center gap-1">
                          <span className="flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Criar Novo Registro
                          </span>
                          <span className="text-xs opacity-70">(outro bebê ou mesma criança com idade diferente)</span>
                        </span>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-center text-[#9890A8] mt-4">
                    Você pode criar um novo registro para outro bebê ou para acompanhar 
                    o desenvolvimento do mesmo bebê em uma idade diferente.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#7BC47F] to-[#5BA35F] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Moon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#30244F]">Encontramos seu plano! 🎉</h3>
                    <p className="text-sm text-[#6B5B8C] mt-2">
                      Plano de sono para <strong>{foundReport.baby_name}</strong>
                    </p>
                  </div>

                  <div className="bg-[#FBF4FF] rounded-xl p-4 mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6B5B8C]">Bebê:</span>
                      <span className="font-medium text-[#30244F]">{foundReport.baby_name}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6B5B8C]">Idade:</span>
                      <span className="font-medium text-[#30244F]">{foundReport.baby_age} meses</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B5B8C]">Status:</span>
                      <span className={`font-medium ${
                        foundReport.status === 'approved' ? 'text-[#7BC47F]' : 'text-[#8F7AC5]'
                      }`}>
                        {foundReport.status === 'approved' ? 'Plano Aprovado ✓' : 
                         foundReport.status === 'pending_approval' ? 'Em aprovação' :
                         foundReport.status === 'pending_routine' ? 'Registrando rotina' : 
                         'Aguardando início'}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleGoToReport}
                    className="w-full bg-[#8F7AC5] hover:bg-[#7A68A8] py-3"
                  >
                    {foundReport.status === 'approved' || foundReport.status === 'pending_approval' 
                      ? 'Ver Meu Plano de Sono' 
                      : 'Continuar Registro'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
